import { useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import { getCompanyComplianceSettings, updateCompanyComplianceSettings } from '../../api/officeandcompany';
import { salaryApi } from '../../api/salary';
import { usePermissionGuard } from '../../hooks/usePermissionGuard';
import { Popup } from '../../components/Popup';

export default function SalaryCompliance() {
  const { showError, showSuccess, popup, closePopup } = usePermissionGuard();
  const [form, setForm] = useState({
    retainerEnabled: false,
    retainerDefaultAmount: 0,
    retainerDefaultPeriod: 3,
    retainerDefaultFloor: 0,
    retainerDefaultRecurring: false,
  });
  const [report, setReport] = useState<{ total_outstanding: number; items: any[] }>({ total_outstanding: 0, items: [] });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await getCompanyComplianceSettings();
      const s = Array.isArray(res) ? res[0] : res?.data ? (Array.isArray(res.data) ? res.data[0] : res.data) : res;
      if (s) {
        setForm((p) => ({
          ...p,
          retainerEnabled: s.retainerEnabled === true || s.retainerEnabled === 1,
          retainerDefaultAmount: Number(s.retainerDefaultAmount) || 0,
          retainerDefaultPeriod: Number(s.retainerDefaultPeriod) || 3,
          retainerDefaultFloor: Number(s.retainerDefaultFloor) || 0,
          retainerDefaultRecurring: s.retainerDefaultRecurring === true || s.retainerDefaultRecurring === 1,
        }));
      }
    } catch {
      /* silent empty-state fallback (no load-failure toast) */
    }
    try {
      const r = await salaryApi.getRetainerReport();
      setReport(r.data || { total_outstanding: 0, items: [] });
    } catch {
      setReport({ total_outstanding: 0, items: [] });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      // POST /employee-tax-config upserts the branch-level row; send the retainer
      // defaults merged onto the existing config (server keeps untouched fields).
      const existing = await getCompanyComplianceSettings();
      const base = Array.isArray(existing) ? existing[0] : existing?.data ?? existing ?? {};
      await updateCompanyComplianceSettings({ ...base, ...form });
      showSuccess('Saved', 'Retainer defaults updated');
    } catch (err: any) {
      showError('Save failed', err?.response?.data?.detail || err?.message || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-full bg-[#faf8f3]">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Wallet className="h-6 w-6 text-emerald-700" />
          <h1 className="text-xl font-semibold">Salary &amp; Compliance</h1>
        </div>

        <div className="bg-white border border-[#e8e3d8] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,45,31,0.04)] space-y-4">
          <label className="flex items-center gap-2 font-medium">
            <input type="checkbox" className="h-4 w-4 text-blue-600 rounded"
              checked={form.retainerEnabled}
              onChange={(e) => setForm({ ...form, retainerEnabled: e.target.checked })} />
            Enable retainer-ship org-wide
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Default amount (₹/mo)</label>
              <input type="number" className="border rounded p-1 text-sm w-full mt-1"
                value={form.retainerDefaultAmount || ''} placeholder="0"
                onChange={(e) => setForm({ ...form, retainerDefaultAmount: e.target.value === '' ? 0 : Number(e.target.value) })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Default period (months)</label>
              <input type="number" min={1} className="border rounded p-1 text-sm w-full mt-1"
                value={form.retainerDefaultPeriod || ''} placeholder="3"
                onChange={(e) => setForm({ ...form, retainerDefaultPeriod: e.target.value === '' ? 0 : Number(e.target.value) })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Take-home floor (₹)</label>
              <input type="number" className="border rounded p-1 text-sm w-full mt-1"
                value={form.retainerDefaultFloor || ''} placeholder="0"
                onChange={(e) => setForm({ ...form, retainerDefaultFloor: e.target.value === '' ? 0 : Number(e.target.value) })} />
            </div>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" className="h-4 w-4 text-blue-600 rounded"
                checked={form.retainerDefaultRecurring}
                onChange={(e) => setForm({ ...form, retainerDefaultRecurring: e.target.checked })} />
              Recurring by default
            </label>
          </div>
          <button onClick={save} disabled={saving}
            className="bg-emerald-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-60">
            {saving ? 'Saving…' : 'Save defaults'}
          </button>
        </div>

        <div className="bg-white border border-[#e8e3d8] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,45,31,0.04)]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Retainer liability</h2>
            <span className="text-sm text-gray-600">Total outstanding: ₹{report.total_outstanding.toLocaleString('en-IN')}</span>
          </div>
          {report.items.length === 0 ? (
            <p className="text-sm text-gray-500">No active retainers.</p>
          ) : (
            <div className="overflow-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500"><th className="sticky top-0 z-10 bg-white py-1">Employee</th><th className="sticky top-0 z-10 bg-white">₹/mo</th><th className="sticky top-0 z-10 bg-white">Period</th><th className="sticky top-0 z-10 bg-white">Outstanding</th></tr></thead>
              <tbody>
                {report.items.map((it) => (
                  <tr key={it.emptID} className="border-t border-[#f0ece2]">
                    <td className="py-1">{it.emptID}</td><td>{it.amount}</td><td>{it.period_months}</td>
                    <td>₹{Number(it.outstanding).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
      <Popup isOpen={popup.isOpen} type={popup.type} title={popup.title} message={popup.message} onClose={closePopup} />
    </div>
  );
}
